import localforage from 'localforage';
import * as Sentry from '@sentry/browser';

const STORED_PHOTO_KIND = 'photo-v3-cache';
export const PHOTO_CACHE_NAME = 'woolacotts-delivery-photos-v1';
const DEFAULT_MIME = 'image/jpeg';

/**
 * Build a deterministic URL to address this photo in Cache Storage.
 * Needs to be a valid same-origin URL so CacheStorage accepts it.
 */
function getPhotoCacheUrl(key) {
  const origin = typeof location !== 'undefined' && location.origin ? location.origin : 'https://woolacotts.local';
  return `${origin}/_photo_cache_/${encodeURIComponent(key)}`;
}

async function getPhotoCache() {
  if (typeof caches === 'undefined') {
    return null;
  }
  try {
    return await caches.open(PHOTO_CACHE_NAME);
  } catch (err) {
    console.warn('CacheStorage not accessible:', err);
    return null;
  }
}

/**
 * Store photos reliably without triggering Chromium's IndexedDB blob backing store bug.
 *
 * Chromium LevelDB wraps any IndexedDB value >= 64KB (kWrapThreshold) into an external
 * blob file. On Android tablets with corrupted LevelDB blob storage or Chrome 129+ regressions,
 * storing large ArrayBuffers or Blobs in IndexedDB throws:
 * "DataError: Failed to write blobs (InvalidBlob)".
 *
 * Solution:
 * 1. Store the binary Blob in the browser's Cache Storage API (uses HTTP Disk Cache, not LevelDB).
 * 2. Store only lightweight metadata (< 300 bytes) in localforage so key enumeration,
 *    indexes, and inspector routines continue to work seamlessly.
 */
export async function savePhoto(key, photo, metadata = {}) {
  const storageEstimate = await getStorageEstimate();
  const photoBlob = await ensureBlob(photo);
  const cache = await getPhotoCache();

  let savedInCache = false;

  if (cache) {
    try {
      const cacheUrl = getPhotoCacheUrl(key);
      const response = new Response(photoBlob, {
        headers: {
          'Content-Type': photoBlob.type || DEFAULT_MIME,
          'Content-Length': String(photoBlob.size)
        }
      });
      await cache.put(new Request(cacheUrl), response);
      savedInCache = true;
    } catch (cacheErr) {
      console.warn('Failed to save photo into CacheStorage, falling back to localforage:', cacheErr);
      Sentry.addBreadcrumb({
        category: 'photo-storage',
        message: 'CacheStorage write failed, falling back',
        level: 'warning',
        data: { key, error: cacheErr?.message }
      });
    }
  }

  // If saved in Cache Storage, we store only lightweight metadata in localforage.
  // Because metadata is ~200 bytes (< 64KB), IndexedDB NEVER triggers Chromium's blob writer!
  let recordToStore;
  if (savedInCache) {
    recordToStore = {
      kind: STORED_PHOTO_KIND,
      key,
      mime: photoBlob.type || DEFAULT_MIME,
      size: photoBlob.size,
      createdAt: Date.now(),
      ...metadata
    };
  } else {
    // Fallback if CacheStorage is completely unavailable: store ArrayBuffer (photo-v2 format)
    const arrayBuffer = await photoBlob.arrayBuffer();
    recordToStore = {
      kind: 'photo-v2',
      key,
      mime: photoBlob.type || DEFAULT_MIME,
      size: photoBlob.size || arrayBuffer.byteLength,
      createdAt: Date.now(),
      data: arrayBuffer,
      ...metadata
    };
  }

  Sentry.addBreadcrumb({
    category: 'photo-storage',
    message: 'saving photo record',
    level: 'info',
    data: {
      key,
      savedInCache,
      size: photoBlob.size,
      mime: photoBlob.type,
      storageUsage: storageEstimate?.usage,
      storageQuota: storageEstimate?.quota
    }
  });

  try {
    await localforage.setItem(key, recordToStore);
  } catch (err) {
    Sentry.captureException(err, {
      tags: { area: 'photo-storage', photo_storage_version: recordToStore.kind },
      extra: {
        key,
        savedInCache,
        ...metadata,
        errorName: err?.name,
        errorMessage: err?.message,
        sourceSize: photoBlob.size,
        sourceType: photoBlob.type,
        storageEstimate
      }
    });
    throw err;
  }

  return recordToStore;
}

/**
 * Retrieve a stored photo as a standard Blob.
 * Fully backwards-compatible with:
 * - photo-v3-cache (Cache Storage binary + localforage metadata)
 * - photo-v2 (localforage ArrayBuffer)
 * - photo-v1 (localforage raw Blob/File)
 */
export async function readPhotoBlob(key) {
  // 1. Try reading from Cache Storage
  const cache = await getPhotoCache();
  let cacheKeysCount = 0;
  if (cache) {
    try {
      const cacheUrl = getPhotoCacheUrl(key);
      let response = await cache.match(cacheUrl);
      if (!response) {
        response = await cache.match(new Request(cacheUrl));
      }
      if (!response) {
        // Fallback: match by relative path in case origin differed
        const relativeUrl = `/_photo_cache_/${encodeURIComponent(key)}`;
        response = await cache.match(relativeUrl);
      }
      if (!response) {
        // Fallback: search cache keys directly (matches encoded or unencoded key)
        const requests = await cache.keys();
        cacheKeysCount = requests.length;
        const encodedKey = encodeURIComponent(key);
        const match = requests.find(req => req.url.includes(encodedKey) || req.url.includes(key));
        if (match) {
          response = await cache.match(match);
        }
      }

      if (response) {
        return await response.blob();
      }
    } catch (cacheReadErr) {
      console.warn('CacheStorage read error, falling back to localforage:', cacheReadErr);
      Sentry.addBreadcrumb({
        category: 'photo-storage',
        message: 'CacheStorage read error during readPhotoBlob',
        level: 'warning',
        data: { key, errorName: cacheReadErr?.name, errorMessage: cacheReadErr?.message }
      });
    }
  }

  // 2. Fallback to localforage (for legacy records or if cache read failed)
  const storedPhoto = await localforage.getItem(key);
  try {
    return normalisePhotoBlob(storedPhoto, key);
  } catch (err) {
    const storageEstimate = await getStorageEstimate();
    const availableCaches = typeof caches !== 'undefined' ? await caches.keys().catch(() => []) : [];
    Sentry.addBreadcrumb({
      category: 'photo-storage',
      message: 'Failed to retrieve photo binary from device',
      level: 'error',
      data: {
        key,
        kind: storedPhoto?.kind,
        storedSize: storedPhoto?.size,
        availableCaches,
        cacheKeysCount,
        storageUsage: storageEstimate?.usage,
        storageQuota: storageEstimate?.quota
      }
    });
    throw err;
  }
}

/**
 * Removes a photo from both Cache Storage and localforage.
 */
export async function deletePhoto(key) {
  if (!key) return;

  const cache = await getPhotoCache();
  if (cache) {
    try {
      const cacheUrl = getPhotoCacheUrl(key);
      await cache.delete(cacheUrl);
      await cache.delete(new Request(cacheUrl));
      const requests = await cache.keys();
      const encodedKey = encodeURIComponent(key);
      const matchingReqs = requests.filter(req => req.url.includes(encodedKey) || req.url.includes(key));
      for (const req of matchingReqs) {
        await cache.delete(req);
      }
    } catch (err) {
      console.warn('Failed to delete photo from CacheStorage:', err);
    }
  }

  try {
    await localforage.removeItem(key);
  } catch (err) {
    console.warn('Failed to delete photo from localforage:', err);
  }
}

/**
 * Clear the entire photo cache (e.g. during settings reset / force update).
 */
export async function clearPhotoStorage() {
  if (typeof caches !== 'undefined') {
    try {
      await caches.delete(PHOTO_CACHE_NAME);
    } catch (err) {
      console.warn('Failed to delete photo cache:', err);
    }
  }
}

/**
 * Normalise any stored photo format into a native Blob.
 */
export function normalisePhotoBlob(storedPhoto, key = 'unknown') {
  if (!storedPhoto) {
    throw new Error(`Missing stored photo for key ${key}`);
  }

  if (typeof Blob !== 'undefined' && storedPhoto instanceof Blob) {
    return storedPhoto;
  }

  if (storedPhoto.data) {
    return new Blob([storedPhoto.data], { type: storedPhoto.mime || DEFAULT_MIME });
  }

  // If it's a photo-v3 metadata record whose cache entry was missing
  if (storedPhoto.kind === STORED_PHOTO_KIND) {
    throw new Error(`Cached photo binary missing for key ${key}`);
  }

  throw new Error(`Unexpected stored photo format for key ${key}`);
}

async function ensureBlob(photo) {
  // If it's an ArrayBuffer, wrap it in a Blob
  if (photo instanceof ArrayBuffer) {
    return new Blob([photo], { type: DEFAULT_MIME });
  }

  // If it's a File (from an <input> element), detach it from the OS file handle by reading into memory
  if (typeof File !== 'undefined' && photo instanceof File) {
    try {
      if (typeof photo.arrayBuffer === 'function') {
        const buffer = await photo.arrayBuffer();
        return new Blob([buffer], { type: photo.type || DEFAULT_MIME });
      }
    } catch (readErr) {
      console.warn('ensureBlob: photo.arrayBuffer() failed on File:', readErr);
    }
  }

  // If it has an arrayBuffer method
  if (photo && typeof photo.arrayBuffer === 'function' && !(photo instanceof Blob && photo.size > 0)) {
    try {
      const buffer = await photo.arrayBuffer();
      return new Blob([buffer], { type: photo.type || DEFAULT_MIME });
    } catch (e) {
      console.warn('ensureBlob: photo.arrayBuffer() fallback failed:', e);
    }
  }

  // If already an in-memory Blob
  if (typeof Blob !== 'undefined' && photo instanceof Blob) {
    return photo;
  }

  throw new Error('Cannot store photo: expected Blob, File, or ArrayBuffer');
}

async function getStorageEstimate() {
  if (!navigator?.storage?.estimate) {
    return null;
  }

  try {
    return await navigator.storage.estimate();
  } catch (err) {
    return null;
  }
}
